import { createTheme } from '@mui/material/styles';
import brandingConfig from '../config/brandingConfig';
import { applyTheme } from '../config/whiteLabel';
import { contrastOn, readableOnDark } from '../utils/contrast';

/**
 * Theme-Fabrik für Hell und Nachtmodus (`mode`: 'light' | 'dark').
 *
 * Die Farben kommen weiterhin aus `brandingConfig`; der Nachtmodus leitet sich
 * daraus ab, statt eine zweite Farbpalette je Instanz zu verlangen:
 * - Hintergründe/Schrift: MUI-Dunkelwerte (die Branding-Textfarben sind für
 *   Weiß gedacht).
 * - Marken- und Sekundärfarbe: werden nur aufgehellt, wenn sie sich auf dem
 *   dunklen Grund nicht mehr abheben (Minicar-Rot, fast schwarzes `secondary`).
 *   Taxi-Gelb bleibt unverändert.
 * - Die Navbar behält die Original-Markenfarbe (Navbar.js), weil die Logos
 *   aller Instanzen für diese Fläche gebaut sind.
 *
 * Siehe docs/NACHTMODUS.md.
 */
const DARK_BACKGROUND = { default: '#121212', paper: '#1E1E1E' };

const colorsFor = (mode) => {
  const { colors } = brandingConfig;
  if (mode !== 'dark') return colors;
  return {
    ...colors,
    primary: readableOnDark(colors.primary, DARK_BACKGROUND.paper),
    secondary: readableOnDark(colors.secondary, DARK_BACKGROUND.paper, 4.5),
  };
};

export const createAppTheme = (mode = 'light') => {
  const dark = mode === 'dark';
  const colors = colorsFor(mode);

  // Create base theme
  const baseTheme = createTheme({
    palette: {
      mode: dark ? 'dark' : 'light',
      primary: {
        main: colors.primary,
        // Generate dark and light variants automatically
        dark: colors.primary,
        light: colors.primary,
        contrastText: contrastOn(colors.primary),
      },
      secondary: {
        main: colors.secondary,
        dark: colors.secondary,
        light: colors.secondary,
        contrastText: contrastOn(colors.secondary),
      },
      error: {
        main: brandingConfig.colors.error,
      },
      warning: {
        main: brandingConfig.colors.warning,
      },
      info: {
        main: brandingConfig.colors.info,
      },
      success: {
        main: brandingConfig.colors.success,
      },
      // Neutrale Flächen (Abschnittsköpfe, Hinweiskästen, Balken). Hell = grey.*
      // wie bisher; `grey.*` selbst bleibt im Nachtmodus hell und ist daher als
      // Hintergrund tabu – stattdessen `bgcolor: 'surface.100'` usw.
      surface: dark
        ? { 50: 'rgba(255, 255, 255, 0.03)', 100: 'rgba(255, 255, 255, 0.06)', 200: 'rgba(255, 255, 255, 0.10)', 300: 'rgba(255, 255, 255, 0.16)' }
        : { 50: '#FAFAFA', 100: '#F5F5F5', 200: '#EEEEEE', 300: '#E0E0E0' },
      // Hintergrund schwebender Kopf-/Fußleisten (Dashboard, Fahrtenliste, RideForm)
      glass: dark
        ? 'linear-gradient(180deg, rgba(30,30,30,0.98), rgba(24,24,24,0.98))'
        : 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(248,250,252,0.98))',
      ...(dark
        ? { background: DARK_BACKGROUND }
        : {
            background: {
              default: '#F5F5F5',
              paper: brandingConfig.colors.background,
            },
            text: {
              primary: brandingConfig.colors.text.primary,
              secondary: brandingConfig.colors.text.secondary,
              disabled: '#9E9E9E',
            },
          }),
    },
    typography: {
      fontFamily: brandingConfig.ui.fontFamily,
      h1: {
        fontWeight: 500,
        fontSize: '2.5rem',
      },
      h2: {
        fontWeight: 500,
        fontSize: '2rem',
      },
      h3: {
        fontWeight: 500,
        fontSize: '1.75rem',
      },
      h4: {
        fontWeight: 500,
        fontSize: '1.5rem',
      },
      h5: {
        fontWeight: 500,
        fontSize: '1.25rem',
      },
      h6: {
        fontWeight: 500,
        fontSize: '1rem',
      },
      button: {
        fontWeight: 500,
        textTransform: 'none',
      },
    },
    shape: {
      borderRadius: parseInt(brandingConfig.ui.borderRadius),
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: brandingConfig.ui.buttonStyle === 'pill' ? 30 : 
                          brandingConfig.ui.buttonStyle === 'square' ? 4 : 8,
            padding: '8px 16px',
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
            },
          },
          containedPrimary: {
            '&:hover': {
              backgroundColor: colors.primary,
              filter: 'brightness(90%)',
            },
          },
          containedSecondary: {
            '&:hover': {
              backgroundColor: colors.secondary,
              filter: dark ? 'brightness(90%)' : 'brightness(110%)',
            },
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: parseInt(brandingConfig.ui.borderRadius) * 1.5,
            boxShadow: dark ? '0px 3px 6px rgba(0, 0, 0, 0.5)' : '0px 3px 6px rgba(0, 0, 0, 0.1)',
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          head: {
            fontWeight: 'bold',
            backgroundColor: dark ? '#2A2A2A' : '#F5F5F5',
          },
        },
      },
    },
  });

  // Apply any additional theme customizations
  return applyTheme(baseTheme, { ...brandingConfig, colors });
};

const theme = createAppTheme('light');

export default theme;